/**
 * Drena a fila de evidências offline: para cada pendente, presign → upload →
 * confirmar → remove da fila. Idempotente o suficiente para retry (cada item só
 * sai da fila após o confirmar).
 *
 * A distinção que sustenta este módulo é entre falha TRANSITÓRIA e PERMANENTE:
 *
 * - transitória (sem rede, 5xx, B2 fora do ar): para a drenagem e devolve o erro
 *   — os pendentes ficam para a próxima tentativa, que é o comportamento certo;
 * - permanente (o servidor recusou o ARQUIVO: 400 grande demais/formato recusado,
 *   413 acima do limite): reenviar dá exatamente o mesmo erro para sempre. Antes,
 *   uma captura assim parava na FRENTE da fila e travava TODAS as evidências
 *   seguintes daquele aparelho, indefinidamente e sem explicar nada — o app só
 *   dizia "sem conexão". Agora o item é marcado com o motivo, pulado, e as
 *   demais evidências sobem normalmente. O arquivo continua na fila (a evidência
 *   nunca se perde): quem decide cancelar é a pessoa, vendo o porquê.
 */
import type { EvidenciaPendente, EvidenciaQueue } from "../persistence/evidenciaQueue.ts";
import { type ApiClient, ApiError } from "./api.ts";

/** Captura que o servidor recusou de vez, com o motivo para mostrar na tela. */
export interface RecusaEvidencia {
  readonly localId: string;
  readonly motivo: string;
}

export interface ResultadoDrenagem {
  readonly enviadas: number;
  readonly recusadas: readonly RecusaEvidencia[];
}

/**
 * Falha que retry NUNCA resolve. Só erro de resposta do NOSSO servidor conta, e
 * só quando fala do ARQUIVO: 400 (grande demais, formato, chave que não bate),
 * 413 (o objeto real no bucket passou do limite).
 *
 * Ficam de fora os 4xx que falam da SESSÃO, não do arquivo — condenar por eles
 * marcaria a fila INTEIRA de uma vez, e falsamente: 401 (expirou; o cliente
 * desloga e a próxima sessão reenvia) e 403 (papel sem escrita — um rebaixamento
 * de papel ou o login numa conta `unidade` não estraga foto nenhuma; ela volta a
 * subir assim que o acesso voltar). Fora também 408/429 (peça de novo depois) e
 * qualquer falha de rede, transitória por definição.
 */
const SESSAO_NAO_ARQUIVO = new Set([401, 403, 408, 429]);

function ehPermanente(e: unknown): boolean {
  if (!(e instanceof ApiError)) {
    return false;
  }
  if (SESSAO_NAO_ARQUIVO.has(e.status)) {
    return false;
  }
  return e.status >= 400 && e.status < 500;
}

function motivoDe(e: unknown): string {
  return e instanceof Error && e.message !== "" ? e.message : "O servidor recusou este arquivo.";
}

export async function drenarEvidencias(
  queue: EvidenciaQueue,
  api: ApiClient,
  token: string,
): Promise<ResultadoDrenagem> {
  const resultado = await subir(await queue.todas(), queue, api, token);
  await drenarExclusoes(queue, api, token);
  return resultado;
}

/**
 * Sobe UMA captura — a que a pessoa acabou de anexar —, sem tocar no resto da
 * fila.
 *
 * Existe por causa do botão: o anexo fica em "Enviando…" até esta promessa
 * resolver. Drenando a fila inteira, quem tivesse acúmulo (um dia de campo
 * offline, dezenas de fotos) ficava MINUTOS com o botão travado e concluía que
 * "não sobe" — mesmo estando tudo funcionando. O acúmulo é trabalho do drenar
 * periódico, que roda em segundo plano; aqui só interessa a foto desta pessoa,
 * agora, e o retorno honesto sobre ela.
 */
export async function drenarEvidencia(
  queue: EvidenciaQueue,
  api: ApiClient,
  token: string,
  localId: string,
): Promise<ResultadoDrenagem> {
  const alvo = (await queue.todas()).filter((p) => p.localId === localId);
  return subir(alvo, queue, api, token);
}

/** Sobe os pendentes informados, um a um, aplicando a regra transitória/permanente. */
async function subir(
  pendentes: readonly EvidenciaPendente[],
  queue: EvidenciaQueue,
  api: ApiClient,
  token: string,
): Promise<ResultadoDrenagem> {
  let enviadas = 0;
  const recusadas: RecusaEvidencia[] = [];
  for (const p of pendentes) {
    if (p.erro) {
      continue; // já recusada: não adianta reenviar, e não pode travar a fila
    }
    try {
      await enviarPendente(p, api, token);
    } catch (e) {
      if (!ehPermanente(e)) {
        throw e; // transitória: o restante da fila fica para a próxima tentativa
      }
      const motivo = motivoDe(e);
      await queue.marcarErro(p.localId, motivo);
      recusadas.push({ localId: p.localId, motivo });
      continue;
    }
    await queue.remover(p.localId);
    enviadas += 1;
  }
  return { enviadas, recusadas };
}

/** presign → PUT no bucket → confirmar. Um item, do começo ao fim. */
async function enviarPendente(p: EvidenciaPendente, api: ApiClient, token: string): Promise<void> {
  const meta = {
    avaliacaoId: p.avaliacaoId,
    itemId: p.itemId,
    momento: p.momento,
    contentType: p.contentType,
    tamanhoBytes: p.blob.size,
  };
  const prep = await api.presignEvidencia(meta, token);
  await enviarAoBucket(() => api.enviarArquivo(prep.uploadUrl, p.blob, p.contentType));
  // A miniatura é acessório: se o upload dela falhar, a evidência (que é o que
  // importa) já subiu e o registro segue sem miniatura — a galeria cai na
  // imagem cheia. Falhar tudo por causa do thumb seria trocar o essencial pelo
  // acessório numa rede de campo instável.
  let chaveMiniatura: string | undefined;
  if (p.blobMiniatura) {
    try {
      await api.enviarArquivo(prep.uploadUrlMiniatura, p.blobMiniatura, p.blobMiniatura.type);
      chaveMiniatura = prep.chaveMiniatura;
    } catch {
      chaveMiniatura = undefined;
    }
  }
  await api.confirmarEvidencia({ id: prep.id, chaveB2: prep.chaveB2, chaveMiniatura, ...meta }, token);
}

/**
 * O PUT vai direto ao bucket, não ao nosso servidor: um 403 ali é URL vencida ou
 * CORS, não "o arquivo está errado". Reembrulha para que NUNCA seja lido como
 * recusa permanente — condenar uma foto por causa de um soluço do B2 seria o
 * erro mais caro possível.
 */
async function enviarAoBucket(enviar: () => Promise<void>): Promise<void> {
  try {
    await enviar();
  } catch (e) {
    throw new Error(`Falha ao enviar o arquivo ao armazenamento: ${motivoDe(e)}`);
  }
}

/**
 * Drena a fila de exclusões: para cada foto marcada, DELETE no servidor → remove
 * da fila. Idempotente (o cliente trata 404 como sucesso), então retry é seguro.
 * Erro transitório propaga — o restante fica para a próxima tentativa; recusa
 * permanente descarta o pedido (repetir daria o mesmo 4xx) sem travar a fila: a
 * foto continua no servidor e reaparece na galeria no próximo pull.
 */
export async function drenarExclusoes(queue: EvidenciaQueue, api: ApiClient, token: string): Promise<number> {
  let excluidas = 0;
  for (const x of await queue.exclusoesPendentes()) {
    try {
      await api.removerEvidencia(x.evidenciaId, x.avaliacaoId, token);
    } catch (e) {
      if (!ehPermanente(e)) {
        throw e;
      }
      await queue.removerExclusao(x.localId);
      continue;
    }
    await queue.removerExclusao(x.localId);
    excluidas += 1;
  }
  return excluidas;
}
